// ============================================================================
// PHASE 6 - SECTION 2: ML MODEL TRAINING DASHBOARD (FRONTEND)
// Phoenix Rescue Apps - Life & Wealth Solutions
// ============================================================================

import React, { useState, useEffect } from 'react';
import { 
  Play, Database, TrendingUp, Cpu, CheckCircle, Clock,
  Activity, BarChart3, Settings, Plus, Eye, Trash2 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const MLModelTrainingDashboard = () => {
  const [models, setModels] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [trainingJobs, setTrainingJobs] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState('models'); // 'models', 'datasets', 'training'
  const [loading, setLoading] = useState(true);
  const [selectedModel, setSelectedModel] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const [dashResp, modelsResp, datasetsResp, jobsResp] = await Promise.all([
        fetch(`${API_URL}/ml/dashboard`),
        fetch(`${API_URL}/ml/models`),
        fetch(`${API_URL}/ml/datasets`),
        fetch(`${API_URL}/ml/training-jobs?limit=10`)
      ]);

      const [dashData, modelsData, datasetsData, jobsData] = await Promise.all([
        dashResp.json(),
        modelsResp.json(),
        datasetsResp.json(),
        jobsResp.json()
      ]);

      if (dashData.success) setDashboardData(dashData.data);
      if (modelsData.success) setModels(modelsData.data);
      if (datasetsData.success) setDatasets(datasetsData.data);
      if (jobsData.success) setTrainingJobs(jobsData.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const startTraining = async (modelId, datasetId) => {
    try {
      const response = await fetch(`${API_URL}/ml/training-jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, datasetId, jobType: 'initial_training' })
      });
      const data = await response.json();
      if (data.success) {
        alert('Training started!');
        loadDashboard();
      }
    } catch (error) {
      console.error('Error starting training:', error);
      alert('Failed to start training');
    }
  };

  const deployModel = async (modelId) => {
    try {
      const response = await fetch(`${API_URL}/ml/models/${modelId}/deploy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ environment: 'production' })
      });
      const data = await response.json();
      if (data.success) {
        alert('Model deployed to production!');
        loadDashboard();
      }
    } catch (error) {
      console.error('Error deploying model:', error);
      alert('Failed to deploy model');
    }
  };

  const renderStats = () => {
    if (!dashboardData) return null;

    const stats = [
      {
        label: 'Total Models',
        value: dashboardData.models.total_models,
        icon: <Cpu size={24} />,
        color: 'blue',
        subtext: `${dashboardData.models.deployed_models} deployed`
      },
      {
        label: 'Ready Datasets',
        value: dashboardData.datasets.total_datasets,
        icon: <Database size={24} />,
        color: 'green'
      },
      {
        label: 'Training Jobs',
        value: dashboardData.training_jobs.total_jobs,
        icon: <Activity size={24} />,
        color: 'purple',
        subtext: `${dashboardData.training_jobs.running_jobs} running`
      },
      {
        label: 'Recent Predictions',
        value: dashboardData.recent_predictions,
        icon: <TrendingUp size={24} />,
        color: 'orange',
        subtext: 'Last 7 days'
      }
    ];

    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, idx) => (
          <div key={idx} className={`bg-${stat.color}-50 border border-${stat.color}-200 rounded-lg p-6`}>
            <div className="flex items-center justify-between mb-3">
              <div className={`w-12 h-12 bg-${stat.color}-500 rounded-lg flex items-center justify-center text-white`}>
                {stat.icon}
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className={`text-xs text-${stat.color}-600 font-medium`}>{stat.label}</p>
              </div>
            </div>
            {stat.subtext && <p className={`text-sm text-${stat.color}-600`}>{stat.subtext}</p>}
          </div>
        ))}
      </div>
    );
  };

  const renderModels = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {models.map(model => (
        <div key={model.id} className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{model.name}</h3>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              model.deployment_status === 'production' ? 'bg-green-100 text-green-800' :
              model.status === 'training' ? 'bg-yellow-100 text-yellow-800' :
              model.status === 'trained' ? 'bg-blue-100 text-blue-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {model.deployment_status === 'production' ? 'Production' : model.status}
            </span>
          </div>
          
          <p className="text-sm text-gray-600 mb-4">{model.description}</p>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-xs text-gray-500 mb-1">Model Type</p>
              <p className="text-sm font-semibold capitalize">{model.model_type.replace(/_/g, ' ')}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-xs text-gray-500 mb-1">Task Type</p>
              <p className="text-sm font-semibold capitalize">{model.task_type}</p>
            </div>
            {model.accuracy && (
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-xs text-gray-500 mb-1">Accuracy</p>
                <p className="text-sm font-semibold">{(model.accuracy * 100).toFixed(2)}%</p>
              </div>
            )}
            {model.f1_score && (
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-xs text-gray-500 mb-1">F1 Score</p>
                <p className="text-sm font-semibold">{(model.f1_score * 100).toFixed(2)}%</p>
              </div>
            )}
          </div>

          <div className="flex space-x-2">
            {model.status === 'draft' && datasets.length > 0 && (
              <button
                onClick={() => startTraining(model.id, datasets[0].id)}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center justify-center space-x-2"
              >
                <Play size={16} />
                <span>Train</span>
              </button>
            )}
            {model.status === 'trained' && model.deployment_status !== 'production' && (
              <button
                onClick={() => deployModel(model.id)}
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center justify-center space-x-2"
              >
                <CheckCircle size={16} />
                <span>Deploy</span>
              </button>
            )}
            <button
              onClick={() => setSelectedModel(model)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              <Eye size={16} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderDatasets = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {datasets.map(dataset => (
        <div key={dataset.id} className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{dataset.name}</h3>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              dataset.status === 'ready' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}>
              {dataset.status}
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-4">{dataset.description}</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-xs text-gray-500 mb-1">Records</p>
              <p className="text-sm font-semibold">{dataset.total_records.toLocaleString()}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-xs text-gray-500 mb-1">Type</p>
              <p className="text-sm font-semibold capitalize">{dataset.dataset_type}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderTrainingJobs = () => (
    <div className="space-y-4">
      {trainingJobs.map(job => (
        <div key={job.id} className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Training Job</h3>
              <p className="text-sm text-gray-600">Model ID: {job.model_id}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              job.status === 'completed' ? 'bg-green-100 text-green-800' :
              job.status === 'running' ? 'bg-blue-100 text-blue-800 animate-pulse' :
              job.status === 'failed' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {job.status}
            </span>
          </div>

          {job.status === 'running' && (
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gray-600">Progress</span>
                <span className="font-semibold">{job.progress_pct}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${job.progress_pct}%` }}
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-xs text-gray-500 mb-1">Type</p>
              <p className="text-sm font-semibold capitalize">{job.job_type.replace(/_/g, ' ')}</p>
            </div>
            {job.final_accuracy && (
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-xs text-gray-500 mb-1">Accuracy</p>
                <p className="text-sm font-semibold">{(job.final_accuracy * 100).toFixed(2)}%</p>
              </div>
            )}
            {job.duration_seconds && (
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-xs text-gray-500 mb-1">Duration</p>
                <p className="text-sm font-semibold">{job.duration_seconds}s</p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Activity className="animate-spin mx-auto mb-4" size={48} />
          <p className="text-gray-600">Loading ML Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">ML Model Training</h1>
        <p className="text-gray-600">Train and deploy custom machine learning models</p>
      </div>

      {renderStats()}

      <div className="mb-6 flex space-x-2">
        {['models', 'datasets', 'training'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-2 rounded-lg font-medium transition-colors capitalize ${
              activeTab === tab
                ? 'bg-blue-500 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'models' && renderModels()}
      {activeTab === 'datasets' && renderDatasets()}
      {activeTab === 'training' && renderTrainingJobs()}
    </div>
  );
};

export default MLModelTrainingDashboard;
